/**
 * AI Generation Service
 * Handles communication with backend AI generation endpoints
 *
 * Two-step generation workflow:
 * 1. Template Selection (~1s): AI analyzes request and selects relevant templates
 * 2. Component Generation (~2-3s): AI generates page components using selected templates
 */

import { api } from '@/services/api/client';
import {
  GenerationRequest,
  GenerationResult,
  GenerationAPIResponse,
  GeneratedPage,
  GenerationMetadata,
  TemplateComponent
} from './types';
import * as errorUtils from '@/utils/errors';

export class AIGenerationService {
  private static instance: AIGenerationService;

  private constructor() {}

  public static getInstance(): AIGenerationService {
    if (!AIGenerationService.instance) {
      AIGenerationService.instance = new AIGenerationService();
    }
    return AIGenerationService.instance;
  }

  private toGenerationStep(step: unknown): 1 | 2 | 3 | 4 {
    return step === 1 || step === 2 || step === 3 || step === 4 ? step : 1;
  }

  /**
   * Generate a complete page using AI
   *
   * @param description - Natural language description of desired page
   * @param projectId - Optional project ID for context
   * @param pageId - Optional page ID for updates
   * @param constraints - Optional generation constraints
   * @returns Promise<GenerationResult> - Generated page with metadata
   *
   * @example
   * const result = await service.generatePage(
   *   "Create a dashboard for monitoring soil moisture sensors",
   *   "project-123"
   * );
   */
  public async generatePage(
    description: string,
    projectId?: string,
    pageId?: string,
    constraints?: GenerationRequest['constraints'],
    context?: GenerationRequest['context']
  ): Promise<GenerationResult> {
    try {
      const startTime = Date.now();

      // Call backend API with extended timeout for AI generation (2 minutes)
      // AI generation involves two steps and can take 30-60 seconds
      const response = await api.post<{ page: GeneratedPage; metadata: GenerationMetadata }>(
        '/ai/generate-page',
        {
          description,
          projectId,
          pageId,
          constraints,
          context
        },
        {
          timeout: 120000 // 2 minutes for AI generation
        }
      );

      const totalDuration = Date.now() - startTime;

      // Handle successful response
      // Backend sends: { success: true, data: { page, metadata } }
      if (response.success && response.data) {
        const { page, metadata } = response.data;

        return {
          success: true,
          page,
          metadata
        };
      }

      // Handle error response
      console.error('❌ AI page generation failed', {
        error: response.error,
        totalDuration
      });

      return {
        success: false,
        error: {
          code: 'GENERATION_FAILED',
          message: response.error || 'Page generation failed',
          step: 1
        }
      };
    } catch (error: unknown) {
      console.error('❌ AI generation error', error);
      const status = errorUtils.getErrorStatus(error);
      const data = errorUtils.getApiErrorRecord(error);

      // Handle network errors
      if (status !== undefined) {
        // Log detailed error for debugging
        console.error('🚨 Backend Error Response:', {
          status,
          fullResponse: data,
          errorMessage: data?.error,
          errorCode: data?.code,
          errorStep: data?.step,
          validationErrors: data?.details
        });

        // If it's a 422 with an error message, show it directly
        if (status === 422 && data?.error) {
          console.error('💥 Generation failed:', data.error);
          if (data?.code) {
            console.error('📍 Error code:', data.code);
          }
          if (data?.step) {
            console.error('🔢 Failed at step:', data.step);
          }
        }

        // Backend returned an error response
        return {
          success: false,
          error: {
            code:
              typeof data?.code === 'string' ? data.code : `HTTP_${status}`,
            message: errorUtils.getApiErrorMessage(error, 'Generation request failed'),
            step: this.toGenerationStep(data?.step),
          }
        };
      }

      // Handle timeout or network errors
      if (
        errorUtils.hasErrorCode(error, 'ECONNABORTED') ||
        errorUtils.getErrorMessage(error).includes('timeout')
      ) {
        return {
          success: false,
          error: {
            code: 'TIMEOUT',
            message: 'Generation request timed out. Please try again.',
            step: 1
          }
        };
      }

      // Generic error
      return {
        success: false,
        error: {
          code: 'UNKNOWN_ERROR',
          message: errorUtils.getErrorMessageOrFallback(
            error,
            'An unexpected error occurred during generation',
          ),
          step: 1
        }
      };
    }
  }

  /**
   * Generate page with simplified interface
   *
   * @param request - Full generation request object
   * @returns Promise<GenerationResult>
   */
  public async generatePageFromRequest(
    request: GenerationRequest
  ): Promise<GenerationResult> {
    return this.generatePage(
      request.description,
      request.projectId,
      request.pageId,
      request.constraints,
      request.context
    );
  }

  /**
   * Check if AI generation service is healthy
   *
   * @returns Promise<boolean> - True if service is healthy
   */
  public async healthCheck(): Promise<boolean> {
    try {
      const response = await api.get<{ success: boolean; status: string }>(
        '/ai/health'
      );
      return response.success && response.data?.status === 'healthy';
    } catch (error) {
      console.error('❌ AI generation health check failed', error);
      return false;
    }
  }

  /**
   * Transform generated components for editor compatibility
   * Ensures components match editor store expectations
   *
   * @param components - Generated components from API
   * @returns Transformed components ready for editor
   */
  public transformComponentsForEditor(
    components: TemplateComponent[]
  ): TemplateComponent[] {
    const chartTypes = [
      'line-chart',
      'bar-chart',
      'pie-chart',
      'area-chart',
      'gauge-chart',
      'heatmap-chart',
      'treemap-chart',
      'scatter-chart',
    ];

    return components.map((component) => {
      const transformed = {
        ...component,
        // Ensure required fields have defaults
        properties: component.properties || {},
        style: component.style || {},
        position: component.position || { x: 0, y: 0, z: 0 },
        size: component.size || { width: '100%', height: 100 },
        children: component.children || []
      };

      // CRITICAL FIX: Ensure chart components have style.height
      // This is a second layer of protection after backend post-processing
      if (chartTypes.includes(component.type)) {
        if (!transformed.style.height && transformed.size.height) {
          const heightValue = typeof transformed.size.height === 'number'
            ? transformed.size.height
            : parseInt(transformed.size.height as string);
          transformed.style.height = `${heightValue}px`;

        }
      }

      return transformed;
    });
  }

  /**
   * Get user-friendly error message from error code
   *
   * @param errorCode - Error code from generation result
   * @returns User-friendly error message
   */
  public getUserFriendlyErrorMessage(errorCode: string): string {
    const errorMessages: Record<string, string> = {
      TIMEOUT: 'The generation request took too long. Please try again with a simpler description.',
      VALIDATION_FAILED: 'Your request contains invalid parameters. Please check and try again.',
      NO_TEMPLATES_MATCHED: 'No suitable templates found for your request. Try a different description.',
      TEMPLATE_SELECTION_ERROR: 'Failed to select appropriate templates. Please try again.',
      COMPONENT_GENERATION_ERROR: 'Failed to generate components. Please try a different description.',
      INSUFFICIENT_CONFIDENCE: 'The AI is not confident about this request. Please provide more details.',
      RATE_LIMIT_EXCEEDED: 'Too many generation requests. Please wait a moment and try again.',
      HTTP_401: 'Authentication required. Please log in and try again.',
      HTTP_403: 'You do not have permission to generate pages.',
      HTTP_422: 'Generation failed. Please try a different description.',
      HTTP_500: 'Server error. Please try again later.',
      HTTP_503: 'AI service is temporarily unavailable. Please try again later.',
      UNKNOWN_ERROR: 'An unexpected error occurred. Please try again.'
    };

    return errorMessages[errorCode] || errorMessages.UNKNOWN_ERROR;
  }
}

// Export singleton instance for convenience
export const aiGenerationService = AIGenerationService.getInstance();
